// Hook to fetch and manage top creators data
import { useState, useEffect } from 'react';
import { useSolPrice } from '../contexts/SolPriceContext';
import { getFormattedTopCreators } from '../utils/creatorStats';
import { caches, createCacheKey } from '../utils/queryCache';

/**
 * Custom hook to fetch top creators with automatic refresh
 * @param {number} limit - Number of creators to fetch
 * @param {number} refreshInterval - Auto-refresh interval in ms (0 to disable)
 * @returns {Object} - { creators, loading, error, refresh }
 */
export const useTopCreators = (limit = 10, refreshInterval = 0) => {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get real-time SOL price from global context
  const { solPrice } = useSolPrice();

  // Fetch creators
  const fetchCreators = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use cache with 5 minute TTL for top creators
      const cacheKey = createCacheKey('top-creators', limit, Math.floor(solPrice || 0));
      const data = await caches.medium.getOrSet(cacheKey, () => getFormattedTopCreators(limit, solPrice));
      
      setCreators(data);
    } catch (err) {
      console.error('Error fetching top creators:', err);
      setError(err.message || 'Failed to fetch top creators');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchCreators();
  }, [limit, solPrice]);

  // Auto-refresh if interval is set
  useEffect(() => {
    if (refreshInterval > 0) {
      const interval = setInterval(fetchCreators, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, limit, solPrice]);

  return {
    creators,
    loading,
    error,
    refresh: fetchCreators
  };
};

